/**
 * @element Button
 * @description THE button. Every clickable action in the dashboard renders
 * through this one component so a primary action looks the same on Home, the
 * Desk and the Console, and so an icon means the same thing everywhere.
 *
 * The contract (docs/brand/MOKSY_LEXICON.md §C9, design-system.md → Buttons):
 *
 *   variant   primary · secondary · outline · ghost · danger · dangerGhost ·
 *             outlineDanger · link · warning · success
 *             — brand orange is the ONLY non-neutral primary; one primary per view.
 *   size      sm · md · lg · icon (square, icon-only) · bare (inline text link)
 *   pill      rounded-full by default; pill={false} for the control radius.
 *   label     one verb, Title Case ("Save", not "Save changes").
 *
 * Icons — the rule that makes them useful:
 *   The same verb ALWAYS gets the same glyph (WCAG 3.2.4, consistent
 *   identification). The verb → glyph map lives in ./buttonIcons.js and is
 *   mirrored in i18n/en/lexicon.json → CTA.ICONS. When `children` is a plain
 *   string whose first word is in the map, primary / secondary / danger /
 *   outline buttons get the icon automatically; ghost and link buttons never do
 *   (an icon there is noise). Pass `icon="name"` to choose explicitly,
 *   `icon={false}` to suppress, or `leftIcon`/`rightIcon` for a custom node.
 *   Icon-only buttons (size="icon") MUST carry an aria-label; the glyph itself
 *   is aria-hidden so screen readers hear the label alone.
 *
 * Usage:
 *   <Button onClick={save}>Save</Button>                       → orange, ✓ icon
 *   <Button variant="outline" size="sm">Cancel</Button>        → bordered, ✕ icon
 *   <Button variant="danger" loading={deleting}>Delete</Button>
 *   <Button variant="ghost" size="icon" icon="close" aria-label="Close" />
 *   <Button variant="link" size="bare">Show more</Button>
 */
import { Children, forwardRef, isValidElement } from 'react';
import clsx from 'clsx';
import { Icon } from './icon.element.jsx';
import { iconForLabel } from './buttonIcons.js';

const VARIANTS = {
  // Brand orange — the one primary action colour. Never black, blue or indigo.
  primary:       'bg-brand text-white hover:bg-brand-dark focus-visible:outline-brand',
  // Deep petrol — high-emphasis secondary (operator surfaces, "uppercase micro-label" buttons).
  secondary:     'bg-brand-navy text-white hover:opacity-90',
  outline:       'border border-gray-300 bg-background text-gray-800 hover:bg-gray-50 hover:border-gray-400',
  ghost:         'text-gray-700 hover:bg-gray-100',
  danger:        'bg-red-600 text-white hover:bg-red-700',
  dangerGhost:   'text-red-700 hover:bg-red-50',
  outlineDanger: 'border border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
  link:          'text-brand underline underline-offset-2 hover:text-brand-dark',
  warning:       'bg-amber-600 text-white hover:bg-amber-500',
  success:       'bg-emerald-600 text-white hover:bg-emerald-500',
};

// Variants where an icon carries meaning. Ghost/link stay text-only.
const ICON_VARIANTS = new Set(['primary', 'secondary', 'outline', 'danger', 'outlineDanger', 'warning', 'success']);

const SIZES = {
  sm:   'px-3 py-1.5 text-xs gap-1.5',
  md:   'px-4 py-2.5 text-sm gap-2',
  lg:   'px-5 py-3 text-base gap-2',
  icon: 'p-1.5 text-sm',
  bare: 'p-0 text-sm gap-1',
};
const ICON_SIZE = { sm: 'h-3.5 w-3.5', md: 'h-4 w-4', lg: 'h-5 w-5', icon: 'h-4 w-4', bare: 'h-3.5 w-3.5' };

const SpinnerGlyph = ({ className }) => (
  <svg className={clsx('animate-spin shrink-0', className)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
  </svg>
);

export const Button = forwardRef(function Button(
  {
    children,
    variant = 'primary',
    size = 'md',
    type = 'button',
    loading = false,
    disabled = false,
    fullWidth = false,
    pill = true,
    icon,
    leftIcon = null,
    rightIcon = null,
    className = '',
    ...props
  },
  ref
) {
  const isDisabled = disabled || loading;
  const glyphClass = ICON_SIZE[size] || ICON_SIZE.md;
  // Does the label carry a node of its own (an icon, a badge) rather than
  // being plain text? Decides how the label wrapper lays out — see below.
  const elementChild = Children.toArray(children).some((c) => isValidElement(c));

  // Resolve the leading glyph: explicit node → explicit name → the verb dictionary → nothing.
  let leading = leftIcon;
  if (!leading && icon !== false) {
    const name = typeof icon === 'string'
      ? icon
      : (ICON_VARIANTS.has(variant) || size === 'icon') && typeof children === 'string'
        ? iconForLabel(children)
        : null;
    if (name) leading = <Icon name={name} className={clsx(glyphClass, 'shrink-0')} />;
  }

  if (process.env.NODE_ENV !== 'production' && size === 'icon' && !props['aria-label'] && !props['aria-labelledby'] && !props.title) {
    // eslint-disable-next-line no-console
    console.warn('[Button] size="icon" needs an aria-label so the action has a name.');
  }

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={clsx(
        'inline-flex items-center justify-center font-medium tracking-tight',
        'transition-colors active:scale-[0.99] select-none',
        size === 'bare' ? '' : pill ? 'rounded-full' : 'rounded-control',
        SIZES[size] || SIZES.md,
        VARIANTS[variant] || VARIANTS.primary,
        fullWidth && 'w-full',
        isDisabled && 'opacity-50 cursor-not-allowed pointer-events-none',
        className
      )}
      {...props}
    >
      {loading ? <SpinnerGlyph className={glyphClass} /> : leading}
      {children != null && children !== '' && (size === 'icon' ? children : (
        /* 🩹 A LABEL THAT CARRIES ITS OWN GLYPH MUST STILL READ AS ONE LINE.
           Tailwind's preflight sets `svg { display: block }`, and this wrapper
           used to be a bare <span>. So any call site that passed its own icon
           NEXT TO its label — `<Button><RestartIcon />Restart</Button>` — got
           both inside one flex item, with the glyph STACKED on top of the word
           and the button's own `gap` never applying (it only separates flex
           children). Walked 2026-09-10: the guest completion panel's "Restart"
           rendered as a 40×30 two-line blob beside three single-line buttons,
           and 30 call sites across the app do the same thing.
           The wrapper lays its children out inline when any of them is an
           element; a plain string keeps the bare span, so nothing that was
           already correct moves by a pixel. */
        elementChild ? <span className="inline-flex items-center gap-1.5">{children}</span> : <span>{children}</span>
      ))}
      {!loading && rightIcon}
    </button>
  );
});

export { VARIANTS as BUTTON_VARIANTS, SIZES as BUTTON_SIZES };
export default Button;
